'use client';

import {Button as BaseButton} from '@base-ui/react/button';
import {clsx} from '@gfiber/typesmith';
import type React from 'react';
import {
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';
import {removeFocus} from '../../../utils/focus.util';
import {CircularProgress} from '../../CircularProgress/v2';
import {Ripple, type RippleRef} from '../../Ripple/Ripple';
import styles from './Button.module.scss';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'text'
  | 'tertiary'
  | 'base'
  | 'brandPrimary';

export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps {
  /** The content of the button */
  children?: ReactNode;
  /** The variant of the button */
  variant?: ButtonVariant;
  /** The size of the button */
  size?: ButtonSize;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Whether the button should have rounded corners */
  round?: boolean;
  /** The type attribute of the button */
  type?: 'button' | 'submit' | 'reset';
  /** Icon to display before the button text */
  icon?: ReactNode;
  /** Icon to display after the button text */
  trailingIcon?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Container wrapper className */
  containerClassName?: string;
  /** Click handler */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** KeyDown handler */
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * Get CSS classes based on button variant, size, and state
 */
function getButtonClasses({
  variant = 'primary',
  size = 'medium',
  round,
  loading,
  disabled,
  className,
}: ButtonProps): string {
  return clsx(
    styles.button,
    styles[variant],
    styles[size],
    {
      [styles.round]: round,
      [styles.loading]: loading,
      [styles.disabled]: disabled,
    },
    className,
  );
}

/**
 * v2 Button component using Base UI
 *
 * A flexible button component with support for different variants, sizes,
 * loading states, and ripple effects.
 */
export const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  round = false,
  type = 'button',
  icon,
  trailingIcon,
  className,
  containerClassName,
  onClick,
  onKeyDown,
  'data-testid': dataTestId,
}: ButtonProps): React.JSX.Element => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const rippleRef = useRef<RippleRef>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      removeFocus(containerRef, onClick, e);

      // Trigger ripple effect for non-base variants
      if (variant !== 'base') {
        rippleRef.current?.trigger(e);
      }
    },
    [onClick, variant],
  );

  const buttonClasses = getButtonClasses({
    variant,
    size,
    round,
    loading,
    disabled,
    className,
  });

  // Show loading spinner or icon
  const iconContent = loading ? (
    <CircularProgress
      label="loading"
      size="xsmall"
      data-testid="button-loading-spinner"
    />
  ) : (
    icon
  );

  return (
    <span
      ref={containerRef}
      className={containerClassName}
    >
      <BaseButton
        type={type}
        disabled={disabled || loading}
        className={buttonClasses}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        data-testid={dataTestId}
      >
        {variant !== 'base' && <Ripple ref={rippleRef} />}
        {iconContent && <span className={styles.icon}>{iconContent}</span>}
        {children && <span className={styles.label}>{children}</span>}
        {trailingIcon && <span className={styles.icon}>{trailingIcon}</span>}
      </BaseButton>
    </span>
  );
};
